import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import english from "../translation/english.json";
import french from "../translation/french.json";
import arabic from "../translation/arabic.json";

export type Language = "en" | "fr" | "ar";

type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  dir: "ltr" | "rtl";
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setInternalLanguage] = useState<Language>(() => {
    // Optimistic load for FCP, but will be overridden by DB setting to ensure "rock solid" sync
    const saved = localStorage.getItem("language");
    return (saved as Language) || "en";
  });
  const location = useLocation();

  const setLanguage = (lang: Language) => {
    setInternalLanguage(lang);
    localStorage.setItem("user_preference", "true");
  };

  // Fetch and apply default language from Database (Source of Truth)
  useEffect(() => {
    const fetchDefaultLanguage = async () => {
      // If the user has explicitly chosen a language, we respect that choice and ignore the default
      if (localStorage.getItem("user_preference")) {
        return;
      }

      try {
        const { data, error } = await supabase
          .from("settings")
          .select("value")
          .eq("key", "language")
          .single();

        if (data?.value?.default) {
          const setting = data.value.default;
          let targetLang: Language = 'en';

          if (setting === 'auto') {
            // Detect browser language
            const browserLang = navigator.language.split('-')[0];
            targetLang = (['en', 'fr', 'ar'].includes(browserLang) ? browserLang : 'en') as Language;
          } else {
            targetLang = setting as Language;
          }

          // We trust the DB setting as the "Default". 
          // If we want to strictly follow "bypassing cache", we update state.
          // However, we should probably respect a user's *explicit* choice if we had a way to track it perfectly.
          // Given the prompt's emphasis on "immediate" and "bypassing cache", we will prioritize the DB-derived default 
          // on the first load of the session, or if the local storage doesn't match a "manual override" flag (which we don't have yet).

          // Implementation: We update the language to the formatted DB default.
          // This ensures if Admin changes 'en' -> 'ar', all users get 'ar' on next reload.
          setInternalLanguage(targetLang);
        }
      } catch (err) {
        console.error("Failed to fetch language settings", err);
      }
    };

    fetchDefaultLanguage();
  }, []);

  useEffect(() => {
    localStorage.setItem("language", language);

    // Admin pages should always be LTR and not affected by public translation settings
    if (location.pathname.startsWith("/admin")) {
      document.documentElement.dir = "ltr";
      // We keep the lang attribute as is or could force it to 'en'
    } else {
      document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
      document.documentElement.lang = language;
    }
  }, [language, location.pathname]);

  const t = (key: string) => {
    const keys = key.split(".");
    let value: any =
      language === "en" ? english :
        language === "fr" ? french :
          arabic;

    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k as keyof typeof value];
      } else {
        return key;
      }
    }

    return typeof value === 'string' ? value : key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, dir: language === "ar" ? "rtl" : "ltr" }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
